import { supabase } from '../lib/supabase';

export const profileService = {
  // Change user password
  async changePassword(newPassword) {
    try {
      const { data, error } = await supabase?.auth?.updateUser({
        password: newPassword
      });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error changing password:', error);
      return { data: null, error };
    }
  },

  // Upload profile photo
  async uploadProfilePhoto(userId, file) {
    try {
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${userId}-${Date.now()}.${fileExt}`;
      const filePath = `${userId}/${fileName}`;

      const { data, error } = await supabase?.storage
        ?.from('profile-photos')
        ?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase?.storage
        ?.from('profile-photos')
        ?.getPublicUrl(filePath);

      return { data: { filePath, publicUrl }, error: null };
    } catch (error) {
      console.error('Error uploading profile photo:', error);
      return { data: null, error };
    }
  },

  // Delete profile photo
  async deleteProfilePhoto(filePath) {
    try {
      const { error } = await supabase?.storage
        ?.from('profile-photos')
        ?.remove([filePath]);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error deleting profile photo:', error);
      return { error };
    }
  },

  // Get user profile by ID
  async getUserProfile(userId) {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('*')
        ?.eq('id', userId)
        ?.single();

      if (error) throw error;

      // Convert snake_case to camelCase
      const profile = {
        id: data?.id,
        email: data?.email,
        fullName: data?.full_name,
        role: data?.role,
        phone: data?.phone,
        city: data?.city,
        state: data?.state,
        profilePhotoUrl: data?.profile_photo_url,
        verifiedStatus: data?.verified_status,
        bio: data?.bio,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };

      return { data: profile, error: null };
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return { data: null, error };
    }
  },

  // Update user profile
  async updateUserProfile(userId, updates) {
    try {
      // Convert camelCase to snake_case
      const dbUpdates = {
        full_name: updates?.fullName,
        phone: updates?.phone,
        city: updates?.city,
        state: updates?.state,
        profile_photo_url: updates?.profilePhotoUrl,
        bio: updates?.bio,
        updated_at: new Date()?.toISOString()
      };

      // Remove undefined values
      Object.keys(dbUpdates)?.forEach(key => 
        dbUpdates?.[key] === undefined && delete dbUpdates?.[key]
      );

      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.update(dbUpdates)
        ?.eq('id', userId)
        ?.select()
        ?.single();

      if (error) throw error;

      // Convert back to camelCase
      const profile = {
        id: data?.id,
        email: data?.email,
        fullName: data?.full_name,
        role: data?.role,
        phone: data?.phone,
        city: data?.city,
        state: data?.state,
        profilePhotoUrl: data?.profile_photo_url,
        verifiedStatus: data?.verified_status,
        bio: data?.bio,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };

      return { data: profile, error: null };
    } catch (error) {
      console.error('Error updating user profile:', error);
      return { data: null, error };
    }
  },

  // Get user statistics
  async getUserStatistics(userId, role) {
    try {
      const stats = {
        activeListings: 0,
        completedRentals: 0,
        totalRentals: 0,
        averageRating: 0,
        totalReviews: 0,
        memberSince: null
      };

      // Get member since date
      const { data: profileData } = await supabase
        ?.from('user_profiles')
        ?.select('created_at')
        ?.eq('id', userId)
        ?.single();

      if (profileData?.created_at) {
        const createdDate = new Date(profileData.created_at);
        stats.memberSince = createdDate?.toLocaleDateString('en-US', { 
          month: 'long', 
          year: 'numeric' 
        });
      }

      // Get completed rentals count (both as owner and renter)
      const { count: completedCount } = await supabase
        ?.from('bookings')
        ?.select('*', { count: 'exact', head: true })
        ?.or(`owner_id.eq.${userId},renter_id.eq.${userId}`)
        ?.eq('status', 'completed');

      stats.completedRentals = completedCount || 0;

      // Get total rentals count
      const { count: totalCount } = await supabase
        ?.from('bookings')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('renter_id', userId);

      stats.totalRentals = totalCount || 0;

      // Get ratings and reviews
      const { data: ratingsData } = await supabase
        ?.from('renter_ratings')
        ?.select('overall_rating')
        ?.eq('renter_id', userId);

      if (ratingsData && ratingsData?.length > 0) {
        const totalRating = ratingsData?.reduce((sum, r) => sum + r?.overall_rating, 0);
        stats.averageRating = (totalRating / ratingsData?.length)?.toFixed(1);
        stats.totalReviews = ratingsData?.length;
      }

      return { data: stats, error: null };
    } catch (error) {
      console.error('Error fetching user statistics:', error);
      return { data: null, error };
    }
  },

  // Get user reviews
  async getUserReviews(userId, limit = 5) {
    try {
      const { data, error } = await supabase
        ?.from('renter_ratings')
        ?.select(`
          *,
          owner:owner_id(full_name, profile_photo_url),
          booking:booking_id(id)
        `)
        ?.eq('renter_id', userId)
        ?.order('created_at', { ascending: false })
        ?.limit(limit);

      if (error) throw error;

      // Convert to camelCase
      const reviews = data?.map(review => ({
        id: review?.id,
        overallRating: review?.overall_rating,
        categoryRatings: review?.category_ratings,
        comment: review?.comment,
        photos: review?.photos,
        createdAt: review?.created_at,
        ownerName: review?.owner?.full_name,
        ownerPhoto: review?.owner?.profile_photo_url
      }));

      return { data: reviews || [], error: null };
    } catch (error) {
      console.error('Error fetching user reviews:', error);
      return { data: [], error };
    }
  }
};